import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, API_BASE } from "../../lib/api";
import { Button } from "../ui/button";
import { PhoneOff, X, Loader2, Mic, MicOff, Volume2 } from "lucide-react";
import { useBranding } from "../../context/BrandingContext";

const ORB_IMAGE = "https://static.prod-images.emergentagent.com/jobs/18d0c04c-e084-4f6e-a055-eee695cb807b/images/da068d154a1ca762d18f22fb17d489f0503087027680424a87e42e17e7dd210f.png";

// VAD aggressiveness 1/2/3 → end-of-speech silence threshold in ms
const VAD_SILENCE_MS = { 1: 1200, 2: 800, 3: 400 };

export default function VoiceCallModal({
  mode,            // 'default' | 'thinking'
  language,        // ISO 639-1
  record,          // bool
  conversationId,
  attachedFileIds,
  selectedModelId,
  selectedModelName,
  vadAggressiveness = 2,
  onClose,
  onConversationCreated,
}) {
  const { t } = useTranslation();
  const branding = useBranding();
  const appName = branding.app_name || "Faro";
  const [status, setStatus] = useState("initializing"); // initializing | connecting | listening | thinking | speaking | error | ended
  const [error, setError] = useState(null);
  const convIdRef = useRef(conversationId);

  // Shared refs
  const micStreamRef = useRef(null);
  // Track EVERY getUserMedia stream we've ever opened so that StrictMode /
  // async race conditions can't leak a mic stream past teardown.
  const allMicStreamsRef = useRef(new Set());
  const audioElRef = useRef(null);
  // Issue 1 — accumulate Realtime transcript events so we can persist them
  // to the conversation on hang-up via POST /realtime/transcript. Each item
  // is { role: 'user'|'assistant', text, ts }.
  const transcriptBufferRef = useRef([]);

  // Path A refs
  const vadRef = useRef(null);
  const audioCtxRef = useRef(null);
  const playQueueRef = useRef([]);
  const playingRef = useRef(false);
  const turnAbortRef = useRef(null);
  const endedRef = useRef(false);
  const currentSourceRef = useRef(null);

  // Default path (Realtime) refs
  const pcRef = useRef(null);
  const dcRef = useRef(null);

  // Recording refs
  const recorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  // Single MediaStreamDestination — fed by mic + AI playback — that the
  // MediaRecorder reads from. Created lazily on the same AudioContext as
  // playback so we never spin up multiple contexts.
  const mixDestRef = useRef(null);
  // Track Realtime session start so we can bill audio-minute usage on hang-up.
  const realtimeStartRef = useRef(null);

  useEffect(() => {
    // Reset refs so React StrictMode's double-invoke (mount → cleanup → remount)
    // doesn't leave endedRef stuck at true, which would make the real hang-up a no-op.
    endedRef.current = false;
    playingRef.current = false;
    playQueueRef.current = [];
    start();
    return () => endCall(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Recording (both modes)
  //
  // Strategy: ONE shared AudioContext (`audioCtxRef`) and ONE shared
  // MediaStreamDestination (`mixDestRef`). Mic + every AI playback source
  // get connected to this destination, and MediaRecorder reads from its
  // .stream. This way the WebM contains both speakers (you + the AI) and
  // Tensornex diarization can label them correctly.
  // -------------------------------------------------------------------------
  const ensureMixDest = async (micStream) => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === "suspended") {
      try { await ctx.resume(); } catch {}
    }
    if (!mixDestRef.current) {
      const dest = ctx.createMediaStreamDestination();
      mixDestRef.current = dest;
      if (micStream && micStream.getAudioTracks().length) {
        try {
          const micSrc = ctx.createMediaStreamSource(micStream);
          micSrc.connect(dest);
        } catch (e) {
          console.warn("Mic → mix wiring failed", e);
        }
      }
    }
    return mixDestRef.current;
  };

  const setupRecorder = async (micStream, remoteStream = null) => {
    if (!record) return;
    // Idempotency guard: StrictMode's mount → cleanup → remount can call us
    // twice — refuse to start a second recorder that would double-write chunks.
    if (recorderRef.current) return;
    try {
      const mixDest = await ensureMixDest(micStream);
      const ctx = audioCtxRef.current;
      if (remoteStream && remoteStream.getAudioTracks && remoteStream.getAudioTracks().length) {
        try {
          const aiSrc = ctx.createMediaStreamSource(remoteStream);
          aiSrc.connect(mixDest);
        } catch (e) {
          console.warn("Could not mix remote stream into recording", e);
        }
      }
      const rec = new MediaRecorder(mixDest.stream, { mimeType: "audio/webm;codecs=opus" });
      recorderRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data?.size) recordChunksRef.current.push(e.data); };
      rec.start(1000);
    } catch (e) {
      console.warn("Recorder setup failed; recording disabled:", e);
    }
  };

  const stopRecorderAndUpload = async () => {
    if (!recorderRef.current || recorderRef.current.state === "inactive") return;
    return new Promise((resolve) => {
      recorderRef.current.onstop = async () => {
        try {
          const blob = new Blob(recordChunksRef.current, { type: "audio/webm" });
          if (blob.size && convIdRef.current) {
            const fd = new FormData();
            fd.append("audio", blob, "call.webm");
            fd.append("conversation_id", convIdRef.current);
            fd.append("language", language || "en");
            try {
              await api.post("/voice/recording", fd, { headers: { "Content-Type": "multipart/form-data" } });
              toast.message(t("voice:live.transcript_pending"));
            } catch {
              // Phase 3 endpoint may not exist yet — fail silently
            }
          }
        } finally {
          resolve();
        }
      };
      recorderRef.current.stop();
    });
  };

  // -------------------------------------------------------------------------
  // Start (dispatch)
  // -------------------------------------------------------------------------
  const start = async () => {
    setStatus("connecting");
    setError(null);
    try {
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      // If the user already ended the call (or StrictMode cleanup fired while
      // we were awaiting getUserMedia), immediately release this stream and bail.
      if (endedRef.current) {
        try { mic.getTracks().forEach((t) => t.stop()); } catch {}
        return;
      }
      allMicStreamsRef.current.add(mic);
      micStreamRef.current = mic;
      if (mode === "thinking") {
        await startPathA(mic);
      } else {
        await startDefault(mic);
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || err.message || t("voice:live.failed");
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      setStatus("error");
      toast.error(t("voice:live.failed"));
    }
  };

  // -------------------------------------------------------------------------
  // PATH A — decoupled (Whisper STT → thinking model → OpenAI TTS)
  // -------------------------------------------------------------------------
  const startPathA = async (mic) => {
    if (!selectedModelId) {
      throw new Error(t("voice:live.no_model"));
    }
    // Eagerly create + unlock the playback AudioContext WITHIN the user-gesture
    // chain (modal opens from a click). If we wait until the first SSE audio
    // chunk arrives, the browser will create a suspended context and the user
    // hears nothing. This also wires mic → mixDest for the recorder.
    try {
      await ensureMixDest(mic);
    } catch (e) {
      console.warn("AudioContext init failed", e);
    }

    // Load VAD
    const vadMod = await import("@ricky0123/vad-web");
    const silenceMs = VAD_SILENCE_MS[vadAggressiveness] || 800;
    const vad = await vadMod.MicVAD.new({
      // CRITICAL: by default vad-web calls navigator.mediaDevices.getUserMedia
      // ITSELF inside start()/resumeStream() — ignoring any `stream` we pass.
      // That leaks a second mic stream Chrome keeps active even after our
      // teardown (the red tab mic-in-use indicator never goes away).
      // We force vad-web to use OUR pre-acquired mic stream by overriding the
      // three stream hooks. Lifetime of the stream is now 100% ours.
      stream: mic,
      getStream: async () => mic,
      pauseStream: async () => { /* keep stream open; vad disconnects its source node internally */ },
      resumeStream: async () => mic,
      onSpeechStart: () => setStatus("listening"),
      onSpeechEnd: async (audio) => {
        // audio is a Float32Array of 16kHz PCM
        setStatus("thinking");
        await sendTurn(audio);
      },
      onVADMisfire: () => {
        // ignored
      },
      positiveSpeechThreshold: 0.5,
      negativeSpeechThreshold: 0.35,
      redemptionFrames: Math.max(4, Math.round(silenceMs / 32)),
      preSpeechPadFrames: 6,
      minSpeechFrames: 4,
      baseAssetPath: "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.30/dist/",
      onnxWASMBasePath: "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.26.0/dist/",
    });
    vadRef.current = vad;
    vad.start();
    setStatus("listening");

    // Setup recording (no AI audio source for path A — we capture via the play queue)
    await setupRecorder(mic, null);
  };

  // Convert Float32Array PCM → WAV blob
  const float32ToWavBlob = (float32, sampleRate = 16000) => {
    const buffer = new ArrayBuffer(44 + float32.length * 2);
    const view = new DataView(buffer);
    const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
    writeStr(0, "RIFF");
    view.setUint32(4, 36 + float32.length * 2, true);
    writeStr(8, "WAVE");
    writeStr(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeStr(36, "data");
    view.setUint32(40, float32.length * 2, true);
    let off = 44;
    for (let i = 0; i < float32.length; i++) {
      const s = Math.max(-1, Math.min(1, float32[i]));
      view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      off += 2;
    }
    return new Blob([buffer], { type: "audio/wav" });
  };

  const sendTurn = async (float32Audio) => {
    try {
      // pause VAD while AI speaks to prevent self-trigger
      vadRef.current?.pause();
      const wav = float32ToWavBlob(float32Audio, 16000);
      const fd = new FormData();
      fd.append("audio", wav, "turn.wav");
      fd.append("model_alias_id", selectedModelId);
      fd.append("language", language || "en");
      fd.append("file_ids", JSON.stringify(attachedFileIds || []));
      if (convIdRef.current) fd.append("conversation_id", convIdRef.current);

      const ctrl = new AbortController();
      turnAbortRef.current = ctrl;

      const token = localStorage.getItem("nexa_token");
      const resp = await fetch(`${API_BASE}/voice/turn`, {
        method: "POST",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
        body: fd,
        signal: ctrl.signal,
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || "Voice turn failed");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      setStatus("speaking");
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const block of parts) {
          let ev = "message";
          let data = "";
          for (const ln of block.split("\n")) {
            if (ln.startsWith("event:")) ev = ln.slice(6).trim();
            else if (ln.startsWith("data:")) data += ln.slice(5).trim();
          }
          if (!data) continue;
          let parsed;
          try { parsed = JSON.parse(data); } catch { continue; }
          if (ev === "meta" && parsed.conversation_id) {
            convIdRef.current = parsed.conversation_id;
            if (onConversationCreated && parsed.conversation_id !== conversationId) {
              try { onConversationCreated(parsed.conversation_id); } catch {}
            }
          } else if (ev === "audio_chunk" && parsed.b64) {
            enqueueAudio(parsed.b64);
          } else if (ev === "error") {
            toast.error(parsed.message || "Turn error");
          } else if (ev === "done") {
            break;
          }
        }
      }
      // Wait for play queue to drain, then resume listening
      await waitForPlaybackEnd();
      setStatus("listening");
      vadRef.current?.start();
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error("turn error", e);
      toast.error("Turn failed");
      setStatus("listening");
      vadRef.current?.start();
    } finally {
      turnAbortRef.current = null;
    }
  };

  const enqueueAudio = (b64) => {
    if (endedRef.current) return;
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    playQueueRef.current.push(arr.buffer);
    drainPlayQueue();
  };

  const drainPlayQueue = async () => {
    if (playingRef.current) return;
    playingRef.current = true;
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioCtxRef.current;
    // Safety: if Chrome auto-suspended the context (autoplay policy), kick it back.
    if (ctx.state === "suspended") {
      try { await ctx.resume(); } catch {}
    }
    while (playQueueRef.current.length && !endedRef.current) {
      const buf = playQueueRef.current.shift();
      try {
        const decoded = await ctx.decodeAudioData(buf.slice(0));
        if (endedRef.current) break;
        await new Promise((resolve) => {
          const src = ctx.createBufferSource();
          currentSourceRef.current = src;
          src.buffer = decoded;
          src.connect(ctx.destination);
          // Also route the AI's audio into the recorder mix so it ends up in
          // the saved WebM — Tensornex diarization will then see two speakers.
          if (mixDestRef.current) {
            try { src.connect(mixDestRef.current); } catch {}
          }
          src.onended = () => {
            currentSourceRef.current = null;
            resolve();
          };
          try { src.start(); } catch { resolve(); }
        });
      } catch (e) {
        console.warn("audio decode failed", e);
      }
    }
    playingRef.current = false;
  };

  const waitForPlaybackEnd = async () => {
    while (playingRef.current || playQueueRef.current.length) {
      await new Promise((r) => setTimeout(r, 100));
    }
  };

  // -------------------------------------------------------------------------
  // DEFAULT path — OpenAI Realtime WebRTC
  // -------------------------------------------------------------------------
  const startDefault = async (mic) => {
    const { data } = await api.post("/realtime/session", {
      conversation_id: conversationId || null,
      file_ids: attachedFileIds || [],
      language,
    });
    convIdRef.current = data.conversation_id;
    // Issue 1 — surface the (possibly newly-created) conv id back to the
    // parent so it lands in the URL / sidebar and future actions reuse it.
    if (onConversationCreated && data.conversation_id && data.conversation_id !== conversationId) {
      try { onConversationCreated(data.conversation_id); } catch {}
    }
    const secret = data?.client_secret;
    if (!secret) throw new Error("No client secret returned by backend");

    const pc = new RTCPeerConnection();
    pcRef.current = pc;
    pc.ontrack = (e) => {
      const remoteStream = e.streams[0];
      if (audioElRef.current) audioElRef.current.srcObject = remoteStream;
      // Mix the AI's remote audio into the recorder the moment it arrives —
      // far more reliable than the old setTimeout/captureStream heuristic.
      setupRecorder(mic, remoteStream).catch(() => {});
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") {
        setStatus("listening");
        if (!realtimeStartRef.current) realtimeStartRef.current = Date.now();
      }
    };
    mic.getTracks().forEach((t) => pc.addTrack(t, mic));
    const dc = pc.createDataChannel("oai-events");
    dcRef.current = dc;
    // Voice-3 — handle the search_conversation function tool call so the
    // Realtime model can do live RAG retrieval mid-call.
    dc.onmessage = async (ev) => {
      let evt;
      try { evt = JSON.parse(ev.data); } catch { return; }
      // Issue 1 — capture user transcript (from Whisper input transcription).
      if (evt.type === "conversation.item.input_audio_transcription.completed") {
        const txt = (evt.transcript || "").trim();
        if (txt) {
          transcriptBufferRef.current.push({
            role: "user", text: txt, ts: new Date().toISOString(),
          });
        }
        return;
      }
      // Issue 1 — capture assistant transcript (audio_transcript.done is the
      // canonical OpenAI Realtime event with the full sentence the model
      // just spoke). We fall back to .delta accumulation only if needed.
      if (evt.type === "response.audio_transcript.done") {
        const txt = (evt.transcript || "").trim();
        if (txt) {
          transcriptBufferRef.current.push({
            role: "assistant", text: txt, ts: new Date().toISOString(),
          });
        }
        return;
      }
      // OpenAI Realtime fires "response.output_item.done" with a function_call item
      // whose .arguments is a JSON string we have to parse.
      if (evt.type === "response.output_item.done" && evt.item?.type === "function_call") {
        const callId = evt.item.call_id;
        const name = evt.item.name;
        let args = {};
        try { args = JSON.parse(evt.item.arguments || "{}"); } catch {}
        if (name === "search_conversation") {
          try {
            const { data: search } = await api.post("/voice/retrieve", {
              conversation_id: convIdRef.current || conversationId,
              query: String(args.query || "").slice(0, 500),
            });
            const formatted = (search.results || [])
              .map((r, i) => `(${i + 1}) [${r.source_type || "context"}] ${r.text}`)
              .join("\n\n");
            // Send tool output back to the model and trigger a fresh response.
            dc.send(JSON.stringify({
              type: "conversation.item.create",
              item: {
                type: "function_call_output",
                call_id: callId,
                output: formatted || "No relevant context found.",
              },
            }));
            dc.send(JSON.stringify({ type: "response.create" }));
          } catch (err) {
            console.warn("search_conversation tool call failed", err);
            dc.send(JSON.stringify({
              type: "conversation.item.create",
              item: {
                type: "function_call_output",
                call_id: callId,
                output: "Search unavailable right now.",
              },
            }));
            dc.send(JSON.stringify({ type: "response.create" }));
          }
        }
      }
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const sdpResp = await fetch("https://api.openai.com/v1/realtime/calls?model=gpt-realtime", {
      method: "POST",
      body: offer.sdp,
      headers: { Authorization: `Bearer ${secret}`, "Content-Type": "application/sdp" },
    });
    if (!sdpResp.ok) {
      const t = await sdpResp.text();
      throw new Error("Realtime SDP exchange failed: " + t.slice(0, 200));
    }
    const answerSdp = await sdpResp.text();
    await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
  };

  // -------------------------------------------------------------------------
  // End call — TEARDOWN ORDER MATTERS
  // -------------------------------------------------------------------------
  const endCall = async (saveRecording = true) => {
    if (endedRef.current) return;
    endedRef.current = true;
    setStatus("ended");

    // Issue 1 — persist accumulated Realtime transcripts to the conversation
    // BEFORE we tear down the data channel. Without this, the chat sidebar
    // shows an empty "New chat" because the transcribed audio never lands
    // as a message row.
    const targetConvId = convIdRef.current || conversationId;
    if (mode === "default" && targetConvId && transcriptBufferRef.current.length > 0) {
      const payload = {
        conversation_id: targetConvId,
        transcript: transcriptBufferRef.current.slice(),
      };
      try {
        await api.post("/realtime/transcript", payload);
        transcriptBufferRef.current = [];
      } catch (err) {
        // Keep the buffer intact so a manual retry / next hang-up can flush
        // it; we don't want a transient network blip to silently lose the
        // user's transcribed conversation.
        console.warn("realtime/transcript save failed (buffer retained)", err);
      }
    }

    // 0. Best-effort: ping the backend with the Realtime session duration so we
    //    can bill audio-minute usage. Fire-and-forget; teardown below proceeds.
    if (mode === "default" && realtimeStartRef.current) {
      const seconds = Math.max(0, (Date.now() - realtimeStartRef.current) / 1000);
      realtimeStartRef.current = null;
      api.post("/realtime/billing", {
        conversation_id: targetConvId || null,
        audio_seconds_in: seconds,
        audio_seconds_out: seconds,  // half-duplex assumption: ~equal in/out
      }).catch(() => {});
    }

    // 1. Stop mic tracks IMMEDIATELY on EVERY stream we've ever opened
    //    (StrictMode / async races can leave more than one alive).
    try {
      for (const s of allMicStreamsRef.current) {
        try { s.getTracks().forEach((t) => t.stop()); } catch {}
      }
      allMicStreamsRef.current.clear();
    } catch {}
    try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    micStreamRef.current = null;

    // 2. Cancel any in-flight /voice/turn request (no more audio chunks coming).
    try { turnAbortRef.current?.abort(); } catch {}

    // 3. Clear queued audio + stop currently-playing source.
    playQueueRef.current = [];
    try { currentSourceRef.current?.stop(0); } catch {}
    currentSourceRef.current = null;

    // 4. Destroy VAD.
    try {
      if (vadRef.current) {
        vadRef.current.destroy();
        vadRef.current = null;
      }
    } catch {}

    // 5. Tear down WebRTC (default mode).
    try { dcRef.current?.close(); } catch {}
    try { pcRef.current?.getSenders().forEach((s) => s.track?.stop()); } catch {}
    try { pcRef.current?.close(); } catch {}

    // 6. Stop MediaRecorder + upload (best-effort; uses already-stopped mic stream).
    if (saveRecording) {
      try { await stopRecorderAndUpload(); } catch {}
    } else {
      try { recorderRef.current?.stop?.(); } catch {}
    }
    recorderRef.current = null;
    mixDestRef.current = null;

    // 7. Close the shared AudioContext (only if not already closed).
    const safeClose = async (ctx) => {
      try { if (ctx && ctx.state !== "closed") await ctx.close(); } catch {}
    };
    await safeClose(audioCtxRef.current);
    audioCtxRef.current = null;

    // 8. Disconnect AI audio element.
    try {
      if (audioElRef.current) {
        audioElRef.current.srcObject = null;
        audioElRef.current.pause();
      }
    } catch {}
  };

  const handleClose = async () => {
    await endCall(true);
    onClose();
  };

  const statusLabel = {
    initializing: t("voice:live.preparing"),
    connecting: t("voice:live.connecting"),
    listening: mode === "thinking" ? t("voice:live.ready") : t("voice:live.listening"),
    thinking: t("voice:live.thinking"),
    speaking: mode === "thinking" ? t("voice:live.ai_paused") : t("voice:live.speaking"),
    error: t("voice:live.error"),
    ended: t("voice:live.ended"),
  }[status];

  return (
    <div className="fixed inset-0 z-50 backdrop-blur-2xl bg-background/85 flex flex-col" data-testid="voice-modal">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">[ {t("voice:live.eyebrow")} ]</div>
          <div className="font-display font-bold tracking-tight text-lg mt-1">
            {t("voice:live.with", "Hablale a")}{" "}
            {mode === "thinking" && selectedModelName
              ? <span className="text-primary">{selectedModelName}</span>
              : <span className="text-primary">{appName}</span>}
            <span className="ml-2 text-xs font-mono text-muted-foreground uppercase">· {language}</span>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={handleClose} data-testid="voice-close-button">
          <X className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex-1 grid place-items-center p-8">
        <div className="text-center max-w-md">
          <div
            className={`relative w-48 h-48 lg:w-72 lg:h-72 mx-auto transition-all ${
              status === "listening" ? "animate-orb-pulse" : ""
            } ${status === "speaking" ? "animate-spin-slow" : ""}`}
            style={status === "speaking" ? { animation: "spin 6s linear infinite", filter: "hue-rotate(30deg)" } : {}}
          >
            <img src={ORB_IMAGE} alt="Voice orb" className="w-full h-full object-contain" />
          </div>

          {/* Status indicator */}
          {status === "speaking" && mode === "thinking" && (
            <div className="mt-6 mx-auto inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-4 py-2 rounded-sm" data-testid="ai-speaking-banner">
              <Volume2 className="h-4 w-4" />
              <span className="text-sm font-medium">{t("voice:live.speaking.label")}</span>
              <MicOff className="h-4 w-4 ml-2 opacity-70" />
            </div>
          )}
          {status === "listening" && mode === "thinking" && (
            <div className="mt-6 mx-auto inline-flex items-center gap-2 border border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400 px-4 py-2 rounded-sm" data-testid="your-turn-banner">
              <Mic className="h-4 w-4" />
              <span className="text-sm font-medium">{t("voice:live.listening.label")}</span>
            </div>
          )}

          <div className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {statusLabel}
          </div>
          {status === "connecting" && (
            <Loader2 className="h-4 w-4 animate-spin mx-auto mt-3 text-muted-foreground" />
          )}
          {error && (
            <div className="mt-4 text-sm text-destructive max-w-md mx-auto whitespace-pre-wrap break-words">
              {error}
            </div>
          )}
          <div className="mt-8 flex items-center justify-center gap-3">
            <Button
              size="lg"
              variant="destructive"
              className="rounded-full h-14 w-14 p-0"
              onClick={handleClose}
              data-testid="voice-end-button"
              aria-label={t("voice:live.end_aria")}
            >
              <PhoneOff className="h-5 w-5" />
            </Button>
          </div>
          <div className="mt-6 text-[11px] font-mono text-muted-foreground max-w-sm mx-auto leading-relaxed">
            {mode === "thinking" && (
              <div className="mb-1.5">{t("voice:live.thinking_tip")}</div>
            )}
            {record ? t("voice:record.on_help") : t("voice:record.off_help")}
          </div>
        </div>
      </div>

      <audio ref={audioElRef} autoPlay hidden />
    </div>
  );
}
